import React, { useEffect, useRef, useState } from "react";
import { cn } from "@src/lib/utils/cn";
import { useChatSidebar } from "@src/features/aiAssist/hooks/useChatSidebar";
import { AiChatType, Ff } from "@src/types.generated";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import useUser from "@src/features/app/hooks/useUser";
import Text from "@src/lib/ui/Text";
import XIcon from "@src/lib/icons/18px/x";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import { Button } from "@src/lib/ui/Button";
import ScrollableFlexGrow from "@src/lib/ui/ScrollableFlexGrow";
import Compose2Icon from "@src/lib/icons/18px/compose-2";
import { OpusAiIcon } from "@src/features/assistantChat/OpusAiIcon";
import { useOpusAgentChat } from "./useOpusAgentChat";
import { OpusAgentThread } from "./OpusAgentThread";
import { OpusAgentComposer } from "./OpusAgentComposer";
import { OpusAgentChatHistoryButton } from "./OpusAgentChatHistoryButton";
import {
  DRAWER_MAX_WIDTH_CSS,
  clampDrawerWidth,
  effectiveDrawerWidth,
  maxDrawerWidthForViewport,
  useOpusAgentDrawerWidthStore,
} from "./useOpusAgentDrawerWidthStore";
import { useOpusAgentDrawerStore } from "./useOpusAgentDrawerStore";

// Admin variant of the Ask Opus drawer, backed by the Opus Agent backend.
// The chrome deliberately duplicates KnowledgeBaseChatSidebar instead of
// reusing it: the state models differ (1s event polling vs the Vercel SSE
// token stream). It shares the Ask Opus open/initial-query store so the
// TopNav button and home hero keep working unchanged.
export const OpusAgentChat: React.FC = () => {
  const {
    isOpen,
    open,
    close,
    conversationId,
    initialQuery,
    clearInitialQuery,
  } = useChatSidebar(AiChatType.KnowledgeBase);
  const { user } = useUser();
  const killSwitchEnabled = useFeatureFlag(Ff.AskOpusKillSwitch);

  // Agent-only persistence of the open state. On mount, a drawer left open
  // reopens without a click; from then on every open/close writes through.
  // The shared store itself stays in-memory (the trainee KB drawer must keep
  // closing on reload). Under the kill switch the persisted value is ignored
  // entirely — neither hydrated (a disabled drawer must not reopen itself
  // after every close) nor written; it resumes once the flag clears. A mount
  // under the flag counts as hydrated, so the flag clearing mid-session
  // never pops the drawer open by itself.
  const openHydratedRef = useRef(false);
  useEffect(() => {
    if (killSwitchEnabled) {
      openHydratedRef.current = true;
      return;
    }
    if (!openHydratedRef.current) {
      openHydratedRef.current = true;
      if (useOpusAgentDrawerStore.getState().isOpen && !isOpen) {
        open();
        return;
      }
    }
    useOpusAgentDrawerStore.getState().setOpen(isOpen);
  }, [isOpen, open, killSwitchEnabled]);

  const {
    turns,
    loading,
    loadFailed,
    isRunning,
    activeChatId,
    sendMessage,
    abort,
    resumePolling,
    retryLoad,
    selectChat,
    startNewChat,
    editMessage,
    effectiveModel,
    defaultModel,
    selectModel,
  } = useOpusAgentChat({
    // Kill switch on = zero background traffic, whenever it lands: no chat
    // lookup, no hydration, no status/events polling — an in-progress
    // session pauses in place and resumes when the switch lifts.
    active: isOpen && !killSwitchEnabled,
    userId: user?.id != null ? String(user.id) : null,
  });
  const [input, setInput] = useState("");
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  // The store's start-new-conversation intent is a fresh conversationId (the
  // KB runtime keys its session on it). The hero mints one before it sets
  // initialQuery; a query arriving under a conversationId this drawer hasn't
  // consumed yet must start a new chat instead of continuing the selected one.
  const consumedConversationIdRef = useRef(conversationId);

  useEffect(() => {
    if (!initialQuery || !initialQuery.trim()) return;
    const startNew = conversationId !== consumedConversationIdRef.current;
    consumedConversationIdRef.current = conversationId;
    if (!killSwitchEnabled) {
      if (startNew) startNewChat();
      sendMessage(initialQuery.trim());
    }
    clearInitialQuery();
  }, [
    initialQuery,
    conversationId,
    sendMessage,
    startNewChat,
    clearInitialQuery,
    killSwitchEnabled,
  ]);

  const handleSubmit = () => {
    sendMessage(input);
    setInput("");
  };

  // Drawer width: the persisted preference, overridden by the live value
  // while the left-edge handle is being dragged. Only the release commits.
  const storedWidth = useOpusAgentDrawerWidthStore((state) => state.width);
  const setStoredWidth = useOpusAgentDrawerWidthStore(
    (state) => state.setWidth,
  );
  const resetStoredWidth = useOpusAgentDrawerWidthStore(
    (state) => state.resetWidth,
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const width = dragWidth ?? storedWidth;
  // The drawer sits on the right: dragging the handle LEFT widens it.
  const widthFromPointer = (clientX: number): number => {
    const drag = dragRef.current;
    if (!drag) return width;
    return clampDrawerWidth(
      drag.startWidth + (drag.startX - clientX),
      maxDrawerWidthForViewport(window.innerWidth),
    );
  };
  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    // Start from the width actually on screen: a stored width the viewport
    // cap is clamping must not make the first pointer move jump.
    const startWidth = effectiveDrawerWidth(width, window.innerWidth);
    dragRef.current = { startX: event.clientX, startWidth };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragWidth(startWidth);
  };
  const moveResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setDragWidth(widthFromPointer(event.clientX));
  };
  const endResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const committed = widthFromPointer(event.clientX);
    dragRef.current = null;
    setDragWidth(null);
    setStoredWidth(committed);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  return (
    <div
      className={cn(
        "shrink-0 overflow-hidden",
        // The open/close width animation must not fight a drag.
        dragWidth === null && "transition-[width] duration-300 ease-in-out",
      )}
      // The viewport cap applies to what renders, not only to drags: a width
      // saved on a large screen is clamped on a small window, live on resize.
      style={{ width: isOpen ? width : 0, maxWidth: DRAWER_MAX_WIDTH_CSS }}
      aria-hidden={!isOpen}
      data-testid="opus-agent-drawer"
    >
      {/* Nothing between the drawer and its panel may be positioned: the
          hairline on the drawer's left edge is <main>'s shadow ring, which
          paints over an in-flow panel (as in KnowledgeBaseChatSidebar) but
          under a positioned one. */}
      <div
        className="flex h-full min-w-0 py-2 pr-2"
        style={{ width, maxWidth: DRAWER_MAX_WIDTH_CSS }}
      >
        {/* Resize handle on the drawer's left edge, in flow and overlapping
            the panel. Positioned so it stays above the panel for hit-testing;
            the 1px left margin keeps the hover/active highlight off the
            hairline. The cursor and the highlight are the whole affordance;
            double-click resets. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat"
          data-testid="opus-agent-drawer-resize-handle"
          className="relative z-10 mr-[-7px] ml-px w-1.5 shrink-0 cursor-col-resize touch-none rounded-r-sm transition-colors hover:bg-state-hover active:bg-state-pressed"
          onPointerDown={startResize}
          onPointerMove={moveResize}
          onPointerUp={endResize}
          onPointerCancel={endResize}
          onDoubleClick={resetStoredWidth}
        />
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl rounded-l-none bg-white shadow-out-sm">
          {killSwitchEnabled ? (
            <div className="relative flex h-full flex-col items-center justify-center p-8">
              <Button
                onClick={close}
                variant="secondary"
                size="icon-xs"
                className="absolute top-2 right-2"
              >
                <XIcon className="size-3 text-muted-foreground" />
              </Button>
              <div className="flex flex-col items-center justify-center gap-2">
                <OpusAiIcon size={48} className="mb-4" />
                <Text variant="compact-title" multiline>
                  Ask Opus is disabled
                </Text>
                <Text
                  variant="compact-title"
                  className="text-center text-muted-foreground"
                  multiline
                >
                  We&apos;re making improvements. Ask Opus will be back online
                  shortly.
                </Text>
              </div>
            </div>
          ) : (
            <div className="relative flex h-full min-w-0 flex-col pb-2">
              <div className="flex min-h-12 items-center justify-between border-b border-gray-2 px-[12px]">
                <Text variant="compact-title">Ask Opus</Text>
                <div className="flex items-center gap-1.5">
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={startNewChat}
                        variant="ghost"
                        size="icon-xs"
                        data-testid="opus-agent-new-chat"
                      >
                        <Compose2Icon className="size-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">
                      Start new chat
                    </TooltipContent>
                  </Tooltip>
                  <OpusAgentChatHistoryButton
                    activeChatId={activeChatId}
                    onSelectChat={selectChat}
                  />
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={close}
                        variant="secondary"
                        size="icon-xs"
                        className="ml-1"
                      >
                        <XIcon className="size-3 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom">Close</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="flex h-full min-w-0 flex-col px-2">
                <ScrollableFlexGrow
                  className="min-w-0 flex-1 overflow-hidden"
                  innerClassName="min-w-0 px-2"
                  innerStyle={{ overflowX: "hidden", overflowY: "auto" }}
                  innerRef={scrollerRef}
                >
                  <OpusAgentThread
                    turns={turns}
                    loading={loading}
                    loadFailed={loadFailed}
                    scrollerRef={scrollerRef}
                    chatKey={activeChatId}
                    onRetryLoad={retryLoad}
                    onSendMessage={sendMessage}
                    onResumePolling={resumePolling}
                    onEditMessage={editMessage}
                  />
                </ScrollableFlexGrow>
                <OpusAgentComposer
                  input={input}
                  onInputChange={setInput}
                  onSubmit={handleSubmit}
                  isRunning={isRunning}
                  onStop={abort}
                  effectiveModel={effectiveModel}
                  defaultModel={defaultModel}
                  onSelectModel={selectModel}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpusAgentChat;
